import { getCurrentUser } from "../auth/service";
import axiosInstance from "../axios";
import { leadMobileFromUrl } from "../store/leadMobileFromUrl";
import { isStore } from "../utils";

export const isInternalAccess = () =>
  !["app.boxs.in", "www.boxs.in", "app.boxs.lc", "boxs.lc"].includes(
    window.location.hostname
  );

export const isStoreSubdomain = () => {
  return isStore();
};

const getFullStoryOrgId = () => (process.env.REACT_APP_FULL_STORY_ORG_ID || "").trim();

const isFullStoryProduction = () =>
  process.env.REACT_APP_ENVIRONMENT === "production" || process.env.NODE_ENV === "production";

export const isFullStoryEnabled = () =>
  isStoreSubdomain() && isFullStoryProduction() && Boolean(getFullStoryOrgId());

/** Known crawlers / preview fetchers — do not init session recording (FullStory, etc.). */
const BOT_UA_SNIPPETS = [
  "facebookexternalhit",
  "facebot",
  "googlebot",
  "google-inspectiontool",
  "googleother",
  "bingbot",
  "msnbot",
  "slurp",
  "duckduckbot",
  "baiduspider",
  "yandexbot",
  "sogou",
  "exabot",
  "ia_archiver",
  "applebot",
  "linkedinbot",
  "twitterbot",
  "pinterest",
  "slackbot",
  "discordbot",
  "whatsapp",
  "telegrambot",
  "petalbot",
  "ahrefsbot",
  "semrushbot",
  "dotbot",
  "mj12bot",
  "bytespider",
  "amazonbot",
  "meta-externalagent",
  "oai-searchbot",
];

export const isLikelyBotUserAgent = () => {
  if (typeof navigator === "undefined" || typeof navigator.userAgent !== "string") {
    return false;
  }
  const ua = navigator.userAgent.toLowerCase();
  if (
    /\b(bot|crawler|spider|scraper)\b/i.test(ua) ||
    /\b(headless|prerender|lighthouse|pingdom|uptime|monitor|checker)\b/i.test(ua)
  ) {
    return true;
  }
  return BOT_UA_SNIPPETS.some((snippet) => ua.includes(snippet));
};

let fullStoryInitialized = false;

const digitsOnly = (raw) => String(raw ?? "").replace(/\D/g, "");

/**
 * National-digit mobile string for FullStory (no country code): strips one or more leading
 * Indian ITU prefixes (91 after optional 0 padding) so URL / API values (`+91 98381…`,
 * `0091…`, `9198…`) all map to the same key. Prefer full length over arbitrary truncation —
 * trailing `slice(-10)` is only a last resort for over-long malformed blobs after prefix strip.
 *
 * Sends values as plain strings everywhere so dashboards don’t treat them as floats (which
 * loses digits for large integers in some tooling).
 */
const canonicalMobileDigitsForFullStory = (raw) => {
  let d = digitsOnly(raw);
  if (!d) return "";

  while (d.startsWith("0")) {
    d = d.slice(1);
  }

  while (d.length > 10 && d.startsWith("91")) {
    d = d.slice(2);
  }

  if (d.length > 10) {
    d = d.slice(-10);
  }

  return d;
};

const isPseudoGuestStoreMobile = (mobile) =>
  String(mobile ?? "")
    .trim()
    .toLowerCase()
    .startsWith("guest_");

const isAuthenticatedStoreUserForFullStory = (user) => {
  if (!user?.id || user.admin || user.temp === true) return false;
  if (isPseudoGuestStoreMobile(user.mobile)) return false;
  return Boolean(canonicalMobileDigitsForFullStory(user.mobile));
};

/**
 * Stable FullStory uid: lead/temp-guest and post-signup buyer share store_mobile_{digits}.
 * @param {Record<string, unknown> | null | undefined} user
 * @param {string} [leadMobile] - From URL or storeLeadMobile when session is still a temp guest.
 */
const fullStoryUidForStoreUser = (user, leadMobile = "") => {
  if (isAuthenticatedStoreUserForFullStory(user)) {
    const key = canonicalMobileDigitsForFullStory(user.mobile);
    if (key) return `store_mobile_${key}`;
  }

  const leadKey = canonicalMobileDigitsForFullStory(leadMobile);
  if (leadKey) return `store_mobile_${leadKey}`;

  if (user?.id) return `store_user_${user.id}`;
  return null;
};

const fullStoryMobileForStoreUser = (user, leadMobile = "") => {
  if (isAuthenticatedStoreUserForFullStory(user)) {
    return canonicalMobileDigitsForFullStory(user.mobile);
  }
  return canonicalMobileDigitsForFullStory(leadMobile);
};

const applyFullStoryIdentify = (identifyPayload) => {
  import("@fullstory/browser")
    .then(async (fullStoryModule) => {
      if (typeof fullStoryModule?.setIdentityAsync === "function") {
        await fullStoryModule.setIdentityAsync(identifyPayload);
        return;
      }

      if (typeof fullStoryModule?.identify === "function") {
        fullStoryModule.identify(identifyPayload.uid, identifyPayload.properties);
        return;
      }

      if (typeof fullStoryModule?.setProperties === "function") {
        fullStoryModule.setProperties({
          type: "user",
          properties: identifyPayload.properties,
        });
        return;
      }

      if (typeof fullStoryModule?.FullStory === "function") {
        await fullStoryModule.FullStory("setIdentityAsync", identifyPayload);
        return;
      }

      console.error("FullStory identify failed: unsupported module shape");
    })
    .catch((error) => {
      console.error("Failed to load FullStory:", error);
    });
};

const initFullStoryWithFallbacks = (fullStoryModule, orgId) => {
  if (typeof fullStoryModule?.init === "function") {
    fullStoryModule.init({ orgId });
    return true;
  }

  if (typeof fullStoryModule?.FullStory === "function") {
    fullStoryModule.FullStory("init", { orgId });
    return true;
  }

  if (typeof fullStoryModule === "function") {
    fullStoryModule("init", { orgId });
    return true;
  }

  return false;
};

export const initFullStory = async () => {
  if (!isFullStoryEnabled() || fullStoryInitialized) return false;
  if (isLikelyBotUserAgent()) return false;

  try {
    const fullStoryModule = await import("@fullstory/browser");
    const orgId = getFullStoryOrgId();

    if (!initFullStoryWithFallbacks(fullStoryModule, orgId)) {
      console.error("FullStory init failed: unsupported module shape");
      return false;
    }

    fullStoryInitialized = true;
    return true;
  } catch (error) {
    console.error("Failed to initialize FullStory:", error);
    return false;
  }
};

/**
 * @param {Record<string, unknown> | null | undefined} explicitUser - Prefer session-derived user so FS updates when localStorage is stale.
 * @param {{ leadMobile?: string }} [options] - Lead mobile from URL or storeLeadMobile for temp-guest sessions.
 */
export const setFullStoryIdentity = (explicitUser, options = {}) => {
  const currentUser = explicitUser ?? getCurrentUser() ?? {};
  const leadMobile = String(options.leadMobile ?? "").trim();

  // FullStory only on store subdomain; skip admins (identified sessions pollute ops recordings).
  if (
    !isStoreSubdomain() ||
    isLikelyBotUserAgent() ||
    currentUser.admin ||
    !isFullStoryEnabled()
  ) {
    return;
  }

  const uid = fullStoryUidForStoreUser(currentUser, leadMobile);
  if (!uid) return;

  const mobileForFs = String(fullStoryMobileForStoreUser(currentUser, leadMobile) || "");
  const isGuest = !isAuthenticatedStoreUserForFullStory(currentUser);
  const displayName = isGuest
    ? mobileForFs || String(currentUser.name ?? "").trim()
    : String(currentUser.name ?? mobileForFs).trim();

  if (!displayName) return;

  applyFullStoryIdentify({
    uid,
    properties: {
      displayName,
      // National digits without +91; keep as string so dashboards don’t coerce to numbers.
      mobile: mobileForFs,
      isGuestUser: isGuest,
    },
  });
};

/** @deprecated Prefer setFullStoryIdentity(null, { leadMobile }) */
export const setFullStoryGuestSessionNameFromMobile = (mobile) => {
  setFullStoryIdentity(null, { leadMobile: mobile });
};

/** Lead mobile for FullStory: URL params first, then persisted storeLeadMobile. */
export const readStoreLeadMobileForFullStory = () => {
  const fromUrl = leadMobileFromUrl();
  if (fromUrl) return fromUrl;

  try {
    const stored = localStorage.getItem("storeLeadMobile");
    return stored != null && String(stored).trim() !== ""
      ? String(stored).trim()
      : "";
  } catch {
    return "";
  }
};

export const randomUUID = () => {
  if (typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  } else {
    const uuid = ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, (c) =>
      (
        c ^
        (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (c / 4)))
      ).toString(16)
    );
    return uuid;
  }
};

export const fetchOrganizationData = async (organizationId) => {
  // Using hard-coded access control since there isn't any plan based access as of now.
  return {
    add_projects: true,
    get_project_cutlist: false,
    get_optimization: false,
    get_bom_summary: false,
    get_item_cutlist: false,
    get_detailed_project_cl_view: false,
    get_material_options: true,
    pricing_mode: "module",
  };
};

export const fetchMaterials = async (type) => {
  return axiosInstance.get(`/materials_price?type=${type}&token=925b4b62202d`);
};

export const fetchTickets = async () => {
  return axiosInstance.get(`/tickets`);
};

export const addTicket = async ({ orderId, subject }) => {
  return axiosInstance.post(`/orders/${orderId}/tickets`, {
    subject,
  });
};

export const updateTicket = async ({ id, key, value }) => {
  return axiosInstance.put(`/tickets/${id}`, {
    key,
    value,
  });
};

export const generateRequestUri = ({ url, params }) => {
  return axiosInstance.getUri({ url, params });
};

export const getAllOrgProjects = async () => {
  return axiosInstance.get(`/organizations`);
};
